import { FC, useCallback } from 'react';
import { DigitalFormsChecklistSvg, DigitalFormsCategorizedListSvg } from '@frontend/digital-forms-assets';
import { BuilderTypes, useBuilderEngineStore } from '@frontend/forms-builder-engine';
import { useTranslation } from '@frontend/i18n';
import { Modal, useModalControl, NakedButton, Text } from '@frontend/design-system';
import { pendoTags } from '../../../../../../../shared/constants';
import { useFormBuilderStore } from '../../../../../provider';
import { PrimaryMedicalHistoryFieldCustomizationData } from '../primary-medical-history-field-customization.type';
import { convertToCategorizedChecklist } from '../primary-medical-history-field-customization.utils';
import { containerStyle, buttonStyle, titleStyle } from './primary-medical-history-field-customization-modal.style';

type CutomizationOptionTranslation = {
  [key in BuilderTypes.FieldTypes.FieldType]: string;
};

interface CutomizationOption {
  type: BuilderTypes.FieldTypes.FieldType;
  image: JSX.Element;
  trackingId: string;
}

const customizationOptions: CutomizationOption[] = [
  {
    type: 'checklist',
    image: <DigitalFormsChecklistSvg />,
    trackingId:
      pendoTags.formBuilder.categories.medicalHistoryFields.customization.customizationOptionModal.fieldViewType
        .defaultChecklist,
  },
  {
    type: 'categorizedChecklist',
    image: <DigitalFormsCategorizedListSvg />,
    trackingId:
      pendoTags.formBuilder.categories.medicalHistoryFields.customization.customizationOptionModal.fieldViewType
        .categorizedChecklist,
  },
];

interface PrimaryMedicalHistoryFieldCustomizationModalProps {
  modalControls: ReturnType<typeof useModalControl>;
  data: PrimaryMedicalHistoryFieldCustomizationData;
}

const PrimaryMedicalHistoryFieldCustomizationModal: FC<PrimaryMedicalHistoryFieldCustomizationModalProps> = ({
  modalControls,
  data,
}) => {
  const { t } = useTranslation('forms');
  const { addField } = useBuilderEngineStore(['addField']);
  const { setUsedPrimaryFieldElement } = useFormBuilderStore(['setUsedPrimaryFieldElement']);
  const translations: Partial<CutomizationOptionTranslation> = {
    categorizedChecklist: t('Categorization with Checkbox'),
    checklist: t('Checkbox Multi-Select'),
  };
  const { closeModal } = modalControls;

  const optionClickHandler = useCallback(
    (type: BuilderTypes.FieldTypes.FieldType, medHxData: PrimaryMedicalHistoryFieldCustomizationData) => {
      if (
        !medHxData ||
        !medHxData.field ||
        !medHxData.sectionId ||
        medHxData.placementIndex === null ||
        !medHxData.primaryFieldKey
      ) {
        console.error('Invalid data for primary medical history field customization', medHxData);
        return;
      }

      switch (type) {
        case 'checklist':
          addField({
            field: medHxData.field,
            sectionId: medHxData.sectionId,
            placementIndex: medHxData.placementIndex,
          });
          break;

        case 'categorizedChecklist':
          addField({
            field: convertToCategorizedChecklist(medHxData.field),
            sectionId: medHxData.sectionId,
            placementIndex: medHxData.placementIndex,
          });
          break;
      }

      setUsedPrimaryFieldElement(medHxData.primaryFieldKey, true);
      closeModal();
    },
    [addField, setUsedPrimaryFieldElement, closeModal]
  );

  return (
    <Modal {...modalControls.modalProps} maxWidth={800}>
      <Modal.Header
        onClose={closeModal}
        closeBtnTrackingId={
          pendoTags.formBuilder.categories.medicalHistoryFields.customization.customizationOptionModal.close
        }
      >
        {t('Select Display Preferences for Medical Conditions')}
      </Modal.Header>
      <Modal.Body>
        <div css={containerStyle}>
          {customizationOptions.map(({ type, image, trackingId }) => (
            <NakedButton
              css={buttonStyle}
              key={type}
              onClick={() => optionClickHandler(type, data)}
              trackingId={trackingId}
            >
              <Text css={titleStyle}>{translations[type]}</Text>
              {image}
            </NakedButton>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PrimaryMedicalHistoryFieldCustomizationModal;
